<template>
  <PublicLayout page-title="Quiénes Somos">

    <div class="about-us-page">
      <!-- Hero Section -->
      <section class="hero-section relative bg-gradient-to-br from-slate-50 to-accent-50 py-24 overflow-hidden">
        <div class="hero-bg-pattern absolute inset-0 opacity-5"></div>
        <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center space-y-8">
            <!-- Trust Badge Strip -->
            <TrustBadgeStrip
              :badges="heroBadges"
              variant="prominent"
              class="mb-8"
            />

            <div class="hero-content space-y-6">
              <h1 class="hero-title text-4xl md:text-6xl font-bold text-text-primary leading-tight">
                El equipo detrás de tu
                <span class="text-gradient bg-gradient-to-r from-accent-600 to-accent-700 bg-clip-text text-transparent">
                  libertad digital
                </span>
              </h1>

              <p class="hero-subtitle text-xl md:text-2xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
                En <span class="text-accent-600 font-semibold">Olvidadme</span> no solo gestionamos datos; devolvemos la soberanía sobre la propia vida.
              </p>

              <!-- Key Metrics -->
              <div class="metrics-row flex flex-wrap justify-center gap-8 pt-8">
                <div
                  v-for="metric in keyMetrics"
                  :key="metric.id"
                  class="metric-item text-center"
                >
                  <div class="metric-number text-3xl md:text-4xl font-bold text-accent-600">
                    {{ metric.value }}
                  </div>
                  <div class="metric-label text-sm text-text-secondary font-medium">
                    {{ metric.label }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Mission & Vision Section -->
      <section class="mission-section py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="space-y-12">
            <!-- Content -->
            <div class="space-y-8 max-w-4xl mx-auto">
              <div class="section-header">
                <div class="flex items-center space-x-3 mb-4">
                  <ScaleIcon class="h-8 w-8 text-accent-600" />
                  <span class="text-sm font-semibold text-accent-600 tracking-wide uppercase">
                    Nuestra Historia
                  </span>
                </div>
                <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary leading-tight">
                  Tu legado, tus reglas
                </h2>
              </div>

              <div class="mission-content space-y-6 text-lg text-text-secondary leading-relaxed">
                <p>
                  Nuestra historia nace de una observación compartida: vivimos en una era donde cada paso
                  que damos deja una huella digital permanente. Desde quienes vimos nacer las primeras
                  redes sociales hasta los nativos digitales de hoy, todos hemos construido un "yo virtual"
                  que nos acompaña siempre, para bien y para mal.
                </p>

                <p>
                  Ante esta realidad, decidimos unir dos mundos imprescindibles para protegerte:
                  <strong>el derecho y la tecnología</strong>.
                </p>
              </div>

              <!-- Vision Points -->
              <div class="vision-points space-y-4">
                <div
                  v-for="point in visionPoints"
                  :key="point.id"
                  class="vision-point flex items-start space-x-4 group"
                >
                  <div class="flex-shrink-0">
                    <component
                      :is="point.icon"
                      class="h-6 w-6 text-accent-600 mt-1"
                    />
                  </div>
                  <div>
                    <h3 class="font-semibold text-text-primary">
                      {{ point.title }}
                    </h3>
                    <p class="text-text-secondary mt-1">
                      {{ point.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Security Card -->
            <div class="max-w-2xl mx-auto">
              <Card class="glassmorphism-card p-8 text-center space-y-6">
                <div class="certification-seal mx-auto w-24 h-24 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center">
                  <ShieldCheckIcon class="h-12 w-12 text-white" />
                </div>

                <div>
                  <h3 class="text-xl font-semibold text-text-primary mb-2">
                    Seguridad y Privacidad
                  </h3>
                  <p class="text-text-secondary">
                    Protegemos tus datos con encriptación de nivel empresarial y las mejores prácticas de seguridad
                  </p>
                </div>

                <!-- Trust Elements -->
                <div class="trust-elements pt-4 border-t border-slate-200">
                  <div class="flex justify-center space-x-6 text-sm text-text-tertiary">
                    <span class="flex items-center">
                      <CheckIcon class="h-4 w-4 mr-1 text-accent-600" />
                      Encriptación AES-256
                    </span>
                    <span class="flex items-center">
                      <CheckIcon class="h-4 w-4 mr-1 text-accent-600" />
                      Datos protegidos
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <!-- Legal Team Section -->
      <section class="team-section py-24 bg-gradient-to-br from-slate-50 to-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="section-header text-center mb-16">
            <div class="flex items-center justify-center space-x-3 mb-4">
              <UsersIcon class="h-8 w-8 text-accent-600" />
              <span class="text-sm font-semibold text-accent-600 tracking-wide uppercase">
                Experiencia y Compromiso
              </span>
            </div>
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              Un equipo con visión clara
            </h2>
            <p class="text-xl text-text-secondary max-w-3xl mx-auto">
              Somos un equipo multidisciplinar con una visión clara: poner la tecnología
              al servicio de las personas y no al revés.
            </p>
          </div>

          <!-- Team Members Grid -->
          <div class="team-grid grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            <Card
              v-for="member in teamMembers"
              :key="member.id"
              class="team-member-card text-left hover:shadow-xl transition-all duration-300 group"
            >
              <div class="p-8 space-y-6">
                <!-- Member Info -->
                <div class="member-info space-y-2">
                  <h3 class="text-xl font-semibold text-text-primary">
                    {{ member.name }}
                  </h3>
                  <p class="text-accent-600 font-medium">
                    {{ member.role }}
                  </p>
                  <p class="text-text-secondary text-sm">
                    {{ member.description }}
                  </p>
                </div>

                <!-- Specializations -->
                <div class="specializations">
                  <div class="flex flex-wrap justify-start gap-2">
                    <span
                      v-for="spec in member.specializations"
                      :key="spec"
                      class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 border border-accent-200"
                    >
                      {{ spec }}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

        </div>
      </section>

      <!-- Expertise Section -->
      <section class="expertise-section py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="section-header text-center mb-16">
            <div class="flex items-center justify-center space-x-3 mb-4">
              <AcademicCapIcon class="h-8 w-8 text-accent-600" />
              <span class="text-sm font-semibold text-accent-600 tracking-wide uppercase">
                Nuestros Servicios
              </span>
            </div>
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              El control de tu identidad debe ser integral
            </h2>
            <p class="text-xl text-text-secondary max-w-3xl mx-auto">
              Por eso, hemos diseñado nuestra propuesta en dos ejes fundamentales.
            </p>
          </div>

          <!-- Expertise Areas -->
          <div class="expertise-grid grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card
              v-for="area in expertiseAreas"
              :key="area.id"
              class="expertise-card p-8 hover:shadow-lg transition-all duration-300 group border-l-4"
              :class="area.borderColor"
            >
              <div class="space-y-6">
                <!-- Icon & Title -->
                <div class="area-header">
                  <div class="flex items-center space-x-4 mb-4">
                    <div class="icon-container p-3 rounded-lg" :class="area.iconBg">
                      <component
                        :is="area.icon"
                        class="h-8 w-8"
                        :class="area.iconColor"
                      />
                    </div>
                    <div>
                      <h3 class="text-xl font-semibold text-text-primary group-hover:text-accent-700 transition-colors duration-200">
                        {{ area.title }}
                      </h3>
                      <p class="text-sm text-text-secondary">
                        {{ area.subtitle }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Description -->
                <p class="text-text-secondary leading-relaxed">
                  {{ area.description }}
                </p>

                <!-- Key Points -->
                <ul class="space-y-2">
                  <li
                    v-for="point in area.keyPoints"
                    :key="point"
                    class="flex items-center text-sm text-text-secondary"
                  >
                    <CheckCircleIcon class="h-4 w-4 text-accent-600 mr-2 flex-shrink-0" />
                    {{ point }}
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <!-- Quote Section -->
      <section class="quote-section py-20 bg-gradient-to-br from-slate-50 to-white">
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote class="text-xl md:text-2xl text-text-secondary italic leading-relaxed">
            "Sabemos que nos enfrentamos a gigantes tecnológicos con recursos casi infinitos,
            pero creemos firmemente en el poder de la justicia y la ética digital.
            <span class="text-accent-600 font-semibold not-italic">Olvidadme</span> es nuestro grano de arena para construir un mundo más humano,
            donde las personas recuperen el derecho a empezar de nuevo y a decidir su propio final digital."
          </blockquote>
          <div class="mt-6 flex items-center justify-center gap-2 text-text-tertiary">
            <span class="w-8 h-px bg-accent-300"></span>
            <span class="text-sm font-medium">Yolanda Moy & Jorge Aznar</span>
            <span class="w-8 h-px bg-accent-300"></span>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <section class="cta-section py-24 bg-gradient-to-r from-accent-600 to-accent-700 text-white">
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div class="cta-content space-y-6">
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              ¿Necesitas proteger tu privacidad digital?
            </h2>
            <p class="text-xl text-accent-100 max-w-2xl mx-auto">
              Nuestro equipo de especialistas está listo para analizar tu caso y ayudarte
              a proteger tu privacidad digital.
            </p>
          </div>

          <!-- CTA Actions -->
          <div class="cta-actions flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              variant="secondary"
              size="lg"
              class="bg-white text-accent-700 hover:bg-accent-50 border-0 font-semibold px-8 py-4 min-w-[200px]"
              @click="handleConsultationRequest"
            >
              <ChatBubbleLeftRightIcon class="h-5 w-5 mr-2" />
              Contactar
            </Button>

            <Button
              variant="secondary"
              size="lg"
              class="bg-white text-accent-700 hover:bg-accent-50 border-2 border-white font-semibold px-8 py-4 min-w-[200px]"
              @click="handleServiceCatalog"
            >
              <DocumentTextIcon class="h-5 w-5 mr-2" />
              Ver servicios
            </Button>
          </div>

          <!-- Trust Elements -->
          <div class="cta-trust flex justify-center items-center pt-8 border-t border-accent-500/30">
            <div class="trust-element flex items-center text-sm text-accent-100">
              <LockClosedIcon class="h-4 w-4 mr-2" />
              100% Confidencial
            </div>
          </div>
        </div>
      </section>
    </div>
  </PublicLayout>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useSeo } from '@/composables/useSeo'

// SEO Meta Tags
useSeo({
  title: 'Quiénes Somos | El equipo detrás de tu libertad digital',
  description: 'Conoce a Yolanda Moy y Jorge Aznar, el equipo fundador de Olvidadme. Más de 20 años de experiencia en derecho y tecnología digital.',
  canonical: '/quienes-somos'
})

// Layout Components
import PublicLayout from '@/components/layout/PublicLayout.vue'

// UI Components
import Button from '@/components/ui/Button.vue'
import Card from '@/components/ui/Card.vue'
import TrustBadgeStrip from '@/components/ui/TrustBadgeStrip.vue'

// Icons
import {
  ScaleIcon,
  ShieldCheckIcon,
  CheckIcon,
  CheckCircleIcon,
  UsersIcon,
  AcademicCapIcon,
  LockClosedIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  CogIcon,
  DocumentDuplicateIcon,
  ShieldExclamationIcon,
  IdentificationIcon
} from '@heroicons/vue/24/outline'

/**
 * AboutUs Component for Olvidadme
 *
 * Implementing Professional Trust Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Creating maximum legal authority and professional credibility
 *
 * Features:
 * - Hero section with trust indicators and key metrics
 * - Mission/Vision with professional guarantees
 * - Legal team showcase with certifications
 * - Expertise areas including foral jurisdictions
 * - Security & compliance section (dark theme)
 * - Strong CTA with consultation offer
 *
 * Follows:
 * - Professional Typography Pattern
 * - Component Pattern for modular sections
 * - Trust Indicators Pattern
 * - Glassmorphism for modern aesthetics
 *
 * SEO optimized with meta tags for legal services
 */

// Router
const router = useRouter()

// Trust badges for hero section
const heroBadges = ref([
  {
    id: 'security',
    text: 'Seguridad AES-256',
    color: 'bg-accent-500'
  }
])

// Key guarantees for hero (real, verifiable)
const keyMetrics = ref([
  { id: 'experience', value: '20+', label: 'Años de experiencia' },
  { id: 'encryption', value: 'AES-256', label: 'Encriptación' }
])

// Vision points - Two main service pillars
const visionPoints = ref([
  {
    id: 'olvido',
    title: 'El Derecho al Olvido',
    description: 'Para que tu pasado no condene tu presente. Actuamos para desindexar y suprimir información que ya no debería estar ahí, protegiendo tu reputación actual.',
    icon: ShieldCheckIcon
  },
  {
    id: 'legado',
    title: 'Planificación del Legado Digital',
    description: 'Porque todos tenemos derecho a decidir qué pasará con nuestra presencia en la red y nuestros activos digitales el día que ya no estemos.',
    icon: DocumentDuplicateIcon
  }
])

// Team members - Real founding team
const teamMembers = ref([
  {
    id: 'legal',
    name: 'Yolanda Moy',
    role: 'Departamento Legal',
    description: 'Abogada colegiada con más de 20 años de trayectoria profesional. Aporta el rigor jurídico, la capacidad de mediación y la firmeza necesaria para defender tus derechos ante las instituciones y los grandes responsables del tratamiento de datos.',
    avatar: ScaleIcon,
    specializations: ['Derecho Digital', 'Protección de Datos', 'Mediación']
  },
  {
    id: 'tech',
    name: 'Jorge Aznar',
    role: 'Desarrollo',
    description: 'Desarrollando productos digitales con más de dos décadas de experiencia. Es quien entiende los entresijos de la red, permitiéndonos localizar y actuar sobre esa huella invisible que otros no ven.',
    avatar: CogIcon,
    specializations: ['Frontend', 'Tecnología', 'Automatización']
  }
])

// Expertise areas - Two main services
const expertiseAreas = ref([
  {
    id: 'olvido',
    title: 'Derecho al Olvido',
    subtitle: 'Art. 17 GDPR',
    description: 'Desindexación y supresión de información personal que ya no debería estar presente, protegiendo tu reputación actual.',
    icon: ShieldExclamationIcon,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100',
    borderColor: 'border-l-blue-500',
    keyPoints: [
      'Procedimientos ante AEPD',
      'Desindexación en buscadores',
      'Supresión de datos personales',
      'Solicitudes a responsables de tratamiento'
    ]
  },
  {
    id: 'legado',
    title: 'Testamento Digital',
    subtitle: 'Legado Digital',
    description: 'Organiza tu futuro digital para que tu memoria sea exactamente como tú deseas.',
    icon: DocumentDuplicateIcon,
    iconColor: 'text-green-600',
    iconBg: 'bg-green-100',
    borderColor: 'border-l-green-500',
    keyPoints: [
      'Gestión de cuentas digitales',
      'Instrucciones para herederos',
      'Activos digitales',
      'Planificación del legado'
    ]
  }
])

// Methods
const handleConsultationRequest = () => {
  router.push('/contacto?type=consultation')
}

const handleServiceCatalog = () => {
  router.push('/servicios')
}
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Hero Section Styles */
.hero-section {
  position: relative;
}

.hero-bg-pattern {
  background-image:
    radial-gradient(circle at 20% 50%, rgba(20, 184, 166, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(20, 184, 166, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(20, 184, 166, 0.1) 0%, transparent 50%);
}

.text-gradient {
  background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.metrics-row {
  animation: fadeInUp 0.8s ease-out 0.3s both;
}

.metric-item {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.metric-item:hover {
  transform: translateY(-4px);
}

/* Glassmorphism Effects */
.glassmorphism-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow:
    0 8px 32px 0 rgba(20, 184, 166, 0.1),
    0 2px 16px 0 rgba(0, 0, 0, 0.02);
}

/* Vision Points Animation */
.vision-point {
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 16px;
  margin: -16px;
}

.vision-point:hover {
  background: rgba(20, 184, 166, 0.05);
  transform: translateX(8px);
}

/* Team Member Cards */
.team-member-card:hover {
  transform: translateY(-8px);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(20, 184, 166, 0.1);
}

.avatar-container {
  position: relative;
}

.avatar-container::before {
  content: '';
  position: absolute;
  inset: -8px;
  background: conic-gradient(from 180deg at 50% 50%, transparent 0deg, rgba(20, 184, 166, 0.3) 180deg, transparent 360deg);
  border-radius: 50%;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.team-member-card:hover .avatar-container::before {
  opacity: 1;
}

/* Expertise Cards */
.expertise-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.area-header {
  position: relative;
}

/* CTA Section */
.cta-section {
  background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%);
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
    line-height: 1.2;
  }

  .hero-subtitle {
    font-size: 1.125rem;
  }

  .metrics-row {
    gap: 1rem;
  }

  .metric-item {
    min-width: 120px;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .metric-item,
  .vision-point,
  .team-member-card,
  .expertise-card {
    transition: none;
  }

  .metric-item:hover,
  .vision-point:hover,
  .team-member-card:hover,
  .expertise-card:hover {
    transform: none;
  }
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .glassmorphism-card {
    background: rgba(255, 255, 255, 1);
    border: 2px solid #0f172a;
  }
}

/* Focus States */
.team-member-card:focus,
.expertise-card:focus {
  outline: 2px solid #14b8a6;
  outline-offset: 4px;
}
</style>
